import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Clock } from "lucide-react";
import { captureReadingTimeOnce } from "../lib/analyticsEvents";

// Approximate reading speed (words per minute). 210 sits comfortably in the
// 200–225 range specified by the editorial brief — closer to attentive
// literary reading than to skimming.
const WORDS_PER_MINUTE = 210;

// Roughly counts words in a piece of running text. Deliberately naive: any
// whitespace-separated token counts. Locale-agnostic; works for EN/PT/ES.
const countWords = (text) => {
  if (!text || typeof text !== "string") return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

// Estimate reading time in whole minutes, minimum 1. Accepts either a string
// or an array of strings (which are concatenated with a space).
export const estimateReadingMinutes = (input) => {
  const text = Array.isArray(input) ? input.filter(Boolean).join(" ") : input;
  const words = countWords(text || "");
  if (words === 0) return 1;
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE));
};

// Discreet reading-time label. Renders "N min read" localised for EN / PT / ES.
// Consumes either a `text` prop (string or array) or a pre-computed `minutes`.
// Renders nothing if there is no readable content — never a "1 min read" for
// a blank article.
export const ReadingTime = ({ text, minutes, testId = "reading-time", className = "", pageType, pageSlug }) => {
  const { t, i18n } = useTranslation();
  const firedRef = useRef(false);
  const mins = typeof minutes === "number" && minutes > 0
    ? Math.max(1, Math.round(minutes))
    : estimateReadingMinutes(text);
  // Reject genuinely empty content.
  const hasText = (
    (typeof text === "string" && text.trim().length > 0) ||
    (Array.isArray(text) && text.some((t) => typeof t === "string" && t.trim().length > 0)) ||
    (typeof minutes === "number" && minutes > 0)
  );
  useEffect(() => {
    if (!hasText) return;
    captureReadingTimeOnce(firedRef, {
      page_type: pageType || null,
      page_slug: pageSlug || null,
      content_language: ((i18n.language || "en").split("-")[0]),
      estimated_reading_minutes: mins,
    });
  }, [hasText, pageType, pageSlug, i18n.language, mins]);
  if (!hasText) return null;
  const label = t("readingTime.label", { minutes: mins, defaultValue: `${mins} min read` });
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-[10px] font-sans tracking-[0.25em] uppercase text-secondary-custom ${className}`}
      aria-label={label}
    >
      <Clock className="w-3 h-3 opacity-70" strokeWidth={1.5} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
};

export default ReadingTime;
